import React from 'react';
import {
	Box,
	VStack,
	Flex,
	Text,
	Image,
	Icon,
	IconButton,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	useDisclosure,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { NavLink } from 'react-router-dom';
import { logout } from '../redux/actions/loginActions';
import { useDispatch } from 'react-redux';
import {
	MdDashboard,
	MdPeople,
	MdBusiness,
	MdShoppingCart,
	MdAssignment,
	MdCategory,
	MdLogout,
	MdBarChart,
	MdHistory,
	MdLocalOffer,
	MdSync,
} from 'react-icons/md';
import logoImage from '../assets/logo.png';

const navItems = [
	{ to: '/admin/dashboard', icon: MdDashboard, label: 'Dashboard' },
	{ to: '/admin/users', icon: MdPeople, label: 'Users' },
	{ to: '/admin/facilities', icon: MdBusiness, label: 'Facilities' },
	{ to: '/admin/products', icon: MdShoppingCart, label: 'Products' },
	{ to: '/admin/categories', icon: MdCategory, label: 'Categories' },
	{ to: '/admin/orders', icon: MdAssignment, label: 'Orders' },
	{ to: '/admin/price-groups', icon: MdLocalOffer, label: 'Price Groups' },
	{ to: '/admin/reports', icon: MdBarChart, label: 'Reports' },
	{ to: '/admin/fishbowl', icon: MdSync, label: 'Fishbowl' },
	{ to: '/admin/auditlog', icon: MdHistory, label: 'Audit Log' },
];

const NavItem = ({ to, icon, label, onClick }: { to: string; icon: any; label: string; onClick?: () => void }) => (
	<NavLink to={to} style={{ textDecoration: 'none', width: '100%' }} onClick={onClick}>
		{({ isActive }) => (
			<Flex
				align="center"
				gap={3}
				px={4}
				py={2.5}
				borderRadius="xl"
				fontWeight="medium"
				fontSize="sm"
				color={isActive ? 'blue.600' : 'gray.600'}
				bg={isActive ? 'blue.50' : 'transparent'}
				_hover={{ bg: isActive ? 'blue.50' : 'gray.100', color: isActive ? 'blue.600' : 'gray.800' }}
				transition="all 0.15s"
				cursor="pointer"
			>
				<Icon as={icon} boxSize={5} flexShrink={0} />
				<Text>{label}</Text>
			</Flex>
		)}
	</NavLink>
);

const SidebarContent = ({ onNavClick }: { onNavClick?: () => void }) => {
	const dispatch = useDispatch();
	const handleLogout = () => dispatch(logout());

	return (
		<Flex direction="column" justify="space-between" h="full" py={5}>
			<Box>
				<Flex justify="center" mb={6} px={5}>
					<NavLink to="/admin/dashboard" onClick={onNavClick}>
						<Image
							src={logoImage}
							alt="Logo"
							maxH="48px"
							transition="transform 0.2s"
							_hover={{ transform: 'scale(1.06)' }}
						/>
					</NavLink>
				</Flex>
				<VStack align="stretch" spacing={1} px={3}>
					{navItems.map((item) => (
						<NavItem key={item.to} {...item} onClick={onNavClick} />
					))}
				</VStack>
			</Box>
			<Box px={3}>
				<Flex
					as="button"
					onClick={handleLogout}
					align="center"
					gap={3}
					w="100%"
					px={4}
					py={2.5}
					borderRadius="xl"
					fontWeight="medium"
					fontSize="sm"
					color="gray.600"
					_hover={{ bg: 'red.50', color: 'red.500' }}
					transition="all 0.15s"
					cursor="pointer"
				>
					<Icon as={MdLogout} boxSize={5} />
					<Text>Logout</Text>
				</Flex>
			</Box>
		</Flex>
	);
};

const AdminNavbar = () => {
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<>
			{/* Desktop sidebar */}
			<Box
				display={{ base: 'none', md: 'flex' }}
				flexDirection="column"
				bg="white"
				w="250px"
				h="100vh"
				position="fixed"
				top="0"
				left="0"
				borderRight="1px solid"
				borderColor="gray.100"
				boxShadow="sm"
				zIndex={100}
			>
				<SidebarContent />
			</Box>

			{/* Mobile top bar */}
			<Flex
				display={{ base: 'flex', md: 'none' }}
				position="fixed"
				top="0"
				left="0"
				right="0"
				zIndex={200}
				bg="white"
				px={4}
				py={3}
				align="center"
				gap={3}
				borderBottom="1px solid"
				borderColor="gray.100"
				boxShadow="sm"
			>
				<IconButton
					aria-label="Open menu"
					icon={<HamburgerIcon />}
					onClick={onOpen}
					variant="ghost"
					size="md"
				/>
				<NavLink to="/admin/dashboard">
					<Image src={logoImage} alt="Logo" maxH="32px" />
				</NavLink>
			</Flex>

			{/* Mobile Drawer */}
			<Drawer isOpen={isOpen} placement="left" onClose={onClose}>
				<DrawerOverlay />
				<DrawerContent maxW="250px">
					<DrawerCloseButton />
					<SidebarContent onNavClick={onClose} />
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default AdminNavbar;
